import { Link } from "react-router-dom";
import styled from "styled-components";
import { Bookmark, MessageSquare, ThumbsUp } from "lucide-react";
import { cPath } from "../../constants";
import { formatDiscountOff, hasAdvertisedDiscount } from "../../utils/offerDisplay";

const Aside = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Card = styled.div`
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  padding: 1rem;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: hsl(${({ theme }) => theme.colors.foreground});
`;

const SeeAll = styled(Link)`
  font-size: 0.75rem;
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.primary});
`;

const Row = styled(Link)`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.25rem 0;
`;

const Thumb = styled.img`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 0.25rem;
  object-fit: cover;
  flex-shrink: 0;
`;

const DealTitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.foreground});
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  ${Row}:hover & {
    color: hsl(${({ theme }) => theme.colors.primary});
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

function DealBlock({ title, deals, showPrices }) {
  if (!deals.length) {
    return (
      <Card>
        <Head>
          <Title>{title}</Title>
          <SeeAll to={cPath("coupons")}>See All</SeeAll>
        </Head>
        <p style={{ margin: 0, fontSize: "0.875rem", color: "hsl(220 10% 46%)" }}>
          No deals to show yet.
        </p>
      </Card>
    );
  }
  return (
    <Card>
      <Head>
        <Title>{title}</Title>
        <SeeAll to={cPath("coupons")}>See All</SeeAll>
      </Head>
      <div>
        {deals.map((deal) => (
          <Row key={deal.id} to={cPath(`coupon/${deal.id}`)}>
            {deal.productImage && <Thumb src={deal.productImage} alt="" />}
            <div style={{ minWidth: 0, flex: 1 }}>
              <DealTitle>{deal.title}</DealTitle>
              {showPrices && (
                <PriceRow>
                  {(deal.salePrice ?? deal.discountedPrice) > 0 && (
                    <>
                      <span
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: 700,
                          color: "hsl(220 40% 13%)",
                        }}
                      >
                        {deal.salePrice ?? deal.discountedPrice}
                      </span>
                      {deal.originalPrice > 0 && (
                        <span
                          style={{
                            fontSize: "0.75rem",
                            textDecoration: "line-through",
                            color: "hsl(220 10% 46%)",
                          }}
                        >
                          {deal.originalPrice}
                        </span>
                      )}
                    </>
                  )}
                  {hasAdvertisedDiscount(deal) ? (
                    <span
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "hsl(348 60% 35%)",
                      }}
                    >
                      {formatDiscountOff(deal)}
                    </span>
                  ) : !(deal.salePrice ?? deal.discountedPrice) ? (
                    <span style={{ fontSize: "0.75rem", color: "hsl(220 10% 46%)" }}>
                      Check current price
                    </span>
                  ) : null}
                </PriceRow>
              )}
              <MetaRow>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                  <ThumbsUp size={12} /> {deal.upvotes ?? 0}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                  <MessageSquare size={12} /> {deal.comments ?? 0}
                </span>
                {deal.isLiked ? (
                  <span
                    style={{ display: "inline-flex", alignItems: "center", gap: 2 }}
                    title="Saved"
                  >
                    <Bookmark size={12} fill="hsl(348 60% 35%)" color="hsl(348 60% 35%)" />
                  </span>
                ) : null}
              </MetaRow>
            </div>
          </Row>
        ))}
      </div>
    </Card>
  );
}

export default function PopularDealsSidebar({
  popularDeals = [],
  trendingDeals = [],
}) {
  const popular = Array.isArray(popularDeals) ? popularDeals : [];
  const trending = Array.isArray(trendingDeals) ? trendingDeals : [];
  return (
    <Aside>
      <DealBlock title="Popular Deals" deals={popular} showPrices />
      <DealBlock title="Trending Deals" deals={trending} showPrices={false} />
    </Aside>
  );
}
